"use client";

import React from "react";
import { Wrench, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InstallationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="93365" className="flex flex-col items-center justify-center py-12 px-6 text-center bg-card/90 backdrop-blur-md rounded-lg border border-border shadow-lg">
        <div className="w-16 h-16 bg-accent text-primary rounded-full flex items-center justify-center mb-4 shadow-sm">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2">預約成功！</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md px-8 border border-primary text-primary hover:bg-accent transition-colors duration-300">
          填寫新需求
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5 bg-card/90 backdrop-blur-md p-8 rounded-lg shadow-lg border border-border relative overflow-hidden">
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      
      <h3 className="text-xl md:text-2xl font-bold text-foreground">填寫預約資訊</h3>

      <FormField control={control} name="name" rules={{
      required: "請填寫您的姓名",
      minLength: {
        value: 2,
        message: "姓名至少需要2個字元"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base font-bold text-foreground">您的姓名</FormLabel>
            <FormControl>
              <Input placeholder="例如：王小明" className="h-12 rounded-md border border-input bg-background focus-visible:border-primary focus-visible:ring-primary/20 shadow-sm transition-colors duration-200" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "請填寫聯絡電話",
      pattern: {
        value: /^[0-9]+$/,
        message: "請輸入有效的聯絡電話"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base font-bold text-foreground">聯絡電話</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="例如：0912345678" className="h-12 rounded-md border border-input bg-background focus-visible:border-primary focus-visible:ring-primary/20 shadow-sm transition-colors duration-200" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "請簡單描述您的需求與服務區域",
      minLength: {
        value: 5,
        message: "請提供更多細節（至少5個字元）"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base font-bold text-foreground">諮詢內容與服務區域</FormLabel>
            <FormControl>
              <Textarea placeholder="請註明您的所在區域（北部/中部/南部）及想諮詢的聽力問題..." className="min-h-[120px] rounded-md border border-input bg-background focus-visible:border-primary focus-visible:ring-primary/20 shadow-sm resize-none transition-colors duration-200" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <div className="text-destructive mt-0.5"></div>
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <div className="space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-md shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 bg-primary text-primary-foreground border border-primary/20" style={{
        boxShadow: isSubmitting ? 'none' : '0 4px 12px hsl(0 72% 45% / 0.2)'
      }}>
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 w-6 h-6" />
              提交中...
            </> : <>
              立即預約專業諮詢
            </>}
        </Button>
        <p className="text-xs text-center text-muted-foreground italic">
          * 提交後最鄰近的聽力中心將盡快與您聯絡確認。
        </p>
      </div>
    </Form>;
}
export default function RangeHoodInstallation() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="聯絡表單" className="w-full bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[30%] h-[30%] rounded-full bg-secondary/5 blur-[80px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative rounded-lg overflow-hidden shadow-2xl border-4 border-card bg-card transform rotate-[-1deg] hover:rotate-0 transition-transform duration-500">
              <div className="absolute inset-0 bg-gradient-to-tr from-black/10 to-transparent z-10 pointer-events-none" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598117/img/kitchen_interior_range_hood_3d.png" alt="專業聽力諮詢服務" className="w-full h-auto object-cover aspect-[4/5] md:aspect-square lg:aspect-[4/5]" />
              
              {}
              <div className="absolute bottom-6 right-6 z-20 bg-card/95 backdrop-blur-sm py-3 px-5 rounded-md shadow-lg border border-border flex items-center gap-3 animate-bounce" style={{
              animationDuration: '4s'
            }}>
                <div className="bg-primary text-primary-foreground p-2 rounded-full">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground leading-tight">專業聽力師</p>
                  <p className="text-xs text-muted-foreground">一對一深度諮詢</p>
                </div>
              </div>
            </div>
            
            {}
            <div className="absolute -z-10 top-10 -left-6 w-24 h-24 bg-secondary/10 rounded-lg rotate-12 shadow-sm" />
            <div className="absolute -z-10 -bottom-8 right-10 w-32 h-32 bg-primary/10 rounded-full shadow-sm" />
          </div>

          {}
          <div ref={contentRef} className={`flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-bold text-sm mb-4 border border-primary/20">
                <Wrench className="w-4 h-4" />
                <span>預約及諮詢</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight tracking-tight">
                專業聽力預約諮詢<br />
                <span className="text-primary">
                  解決您的聽力煩惱
                </span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                專為追求生活品質的您提供專業聽力檢測與助聽器適配方案。無論是初步檢測還是進階調整，我們的專業團隊都能為您提供最貼心的服務。
              </p>
              <p className="mt-6 text-xl font-bold text-red-600 leading-snug">
                全台各地區100+聽力中心實體門市服務，請填寫需求表單 我們將安排最近的聽力中心門市與您聯絡!
              </p>
            </div>

            <FormProvider formKey="contact_form_general" formId="contact_form_general" sectionName="HearingAidConsultation" defaultValues={{
            name: "",
            phone: "",
            message: ""
          }} submitText="立即預約專業諮詢" successMessage="感謝您的預約！我們的聽力諮詢專員將於24小時內與您聯絡確認細節。">
              <InstallationForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}